/**
 * The Tasks board's wire types, mirroring `apps/server/src/types/task.ts`.
 *
 * Dates arrive as JSON strings rather than the `Date` the server types name,
 * and are sent back as `YYYY-MM-DD`.
 */

/** The states a task can be moved to. `CANCELLED` is shown as Dismissed. */
export const TASK_STATUSES = [
  'PENDING',
  'IN_PROGRESS',
  'IN_REVIEW',
  'COMPLETED',
  'CANCELLED',
] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

/** A person as they appear attached to a task. Deliberately without an email. */
export interface AssignedUser {
  id: string;
  display_name: string;
  image_url: string | null;
}

export interface TaskLabel {
  id: string;
  name: string;
}

/** The conversation a task came out of, carrying its title so a link needs no second call. */
export interface TaskSource {
  workflow_execution_id: string;
  title: string | null;
}

export interface Task {
  id: string;
  key: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: string | null;
  start_date: string | null;
  due_date: string | null;
  creator: AssignedUser | null;
  assignee: AssignedUser | null;
  reviewer: AssignedUser | null;
  labels: TaskLabel[];
  source: TaskSource | null;
  created_at: string;
}

/**
 * The fields a caller may change.
 *
 * An absent key leaves the field alone and an explicit null clears it, which is
 * the difference between reassigning a task and unassigning it. Labels are
 * replaced wholesale, so send the full set the task should end up with.
 */
export interface TaskPatch {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  assignee_user_id?: string | null;
  reviewer_user_id?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  labels?: string[];
}

export interface TaskCreateInput {
  title: string;
  description?: string | null;
  status?: TaskStatus;
  assignee_user_id?: string | null;
  reviewer_user_id?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  labels?: string[];
}

/** A workspace member, as the assignee and reviewer pickers need them. */
export interface TenantUser {
  id: string;
  email: string | null;
  display_name: string;
  image_url: string | null;
}

/**
 * What the board asks the server for. Every narrowing happens here, not after.
 *
 * The value filters are lists because the menu behind them is a checklist: a
 * checkbox that only ever holds one value is a radio button wearing the wrong
 * shape. An empty list and an absent one both mean the filter is off.
 */
export interface TaskListQuery {
  status?: TaskStatus[];
  assignee_user_id?: string[];
  reviewer_user_id?: string[];
  creator_user_id?: string[];
  label_id?: string[];
  source_workflow_execution_id?: string[];
  /** Rows asked for by their emptiness, which an id filter cannot express. */
  no_assignee?: boolean;
  no_reviewer?: boolean;
  no_labels?: boolean;
  no_start_date?: boolean;
  no_due_date?: boolean;
  /** Inclusive `YYYY-MM-DD` bounds, either end optional. */
  start_date_from?: string;
  start_date_to?: string;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export interface TaskListResponse {
  tasks: Task[];
  total: number;
}

/** How many tasks sit behind each value of one filter. */
export interface TaskFacetBuckets {
  none: number;
  by_id: Record<string, number>;
}

/**
 * The conversations tasks came out of, carrying their titles.
 *
 * The one dimension that names its values: nothing else lists the
 * conversations that produced tasks, so an id-keyed map would leave the menu
 * with nothing to render.
 */
export interface TaskSourceFacet {
  id: string;
  title: string | null;
  count: number;
}

/** The counts behind every filter the board offers. */
export interface TaskFacets {
  status: Record<string, number>;
  assignee: TaskFacetBuckets;
  creator: TaskFacetBuckets;
  reviewer: TaskFacetBuckets;
  labels: TaskFacetBuckets;
  source: TaskSourceFacet[];
}
